import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';
import { AuthService } from '../../auth/auth.service';
import { OfflineDataService } from './offline-data.service';

@Injectable({
  providedIn: 'root',
})
export class SyncSchedulerService {
  private readonly quietPeriodMs = 15000;
  private readonly retryDelayMs = 15000;
  private readonly idleTimeoutMs = 10000;

  private pendingSync = false;
  private isSyncing = false;
  private timer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private offlineDataService: OfflineDataService,
    private authService: AuthService
  ) {}

  markDirty(reason: string): void {
    this.pendingSync = true;
    this.schedule(this.quietPeriodMs, reason);
  }

  flushNow(reason: string): Observable<boolean> {
    this.pendingSync = true;
    this.clearTimer();

    if (!this.canSyncNow()) {
      this.schedule(this.retryDelayMs, reason);
      return of(false);
    }

    return this.runSync(reason);
  }

  hasPendingSync(): boolean {
    return this.pendingSync;
  }

  private schedule(delayMs: number, reason: string): void {
    this.clearTimer();
    this.timer = setTimeout(() => {
      this.timer = null;
      this.runWhenIdle(reason);
    }, delayMs);
  }

  private runWhenIdle(reason: string): void {
    if (!this.canSyncNow()) {
      this.schedule(this.retryDelayMs, reason);
      return;
    }

    const requestIdleCallback = (
      window as Window &
        typeof globalThis & {
          requestIdleCallback?: (
            callback: () => void,
            options?: { timeout: number }
          ) => number;
        }
    ).requestIdleCallback;

    if (requestIdleCallback) {
      requestIdleCallback(
        () => {
          this.runSync(reason).subscribe();
        },
        { timeout: this.idleTimeoutMs }
      );
      return;
    }

    setTimeout(() => {
      this.runSync(reason).subscribe();
    });
  }

  private runSync(reason: string): Observable<boolean> {
    this.isSyncing = true;
    const startedAt = Date.now();

    return this.offlineDataService.syncOfflineData().pipe(
      tap((success) => {
        if (success) {
          this.pendingSync = false;
        }
        console.info(
          `Spark sync completed. reason=${reason}; success=${success}; elapsedMs=${
            Date.now() - startedAt
          }`
        );
      }),
      catchError((error) => {
        this.pendingSync = true;
        console.warn('Spark sync deferred after failure', {
          reason,
          elapsedMs: Date.now() - startedAt,
          error,
        });
        this.schedule(this.retryDelayMs, reason);
        return of(false);
      }),
      finalize(() => {
        this.isSyncing = false;
      })
    );
  }

  private canSyncNow(): boolean {
    return (
      this.pendingSync &&
      !this.isSyncing &&
      !this.offlineDataService.isInOfflineMode() &&
      this.authService.isSignedIn()
    );
  }

  private clearTimer(): void {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }
}
